import React from 'react';
import { X, Trash2, AlertTriangle, PenTool } from 'lucide-react';

interface DeleteConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  isDeleting?: boolean;
  deleteButtonText?: string;
  itemType?: string;
  customStyling?: {
    iconColor?: string;
    iconBg?: string;
    confirmButtonBg?: string;
    confirmButtonText?: string;
    useSignIcon?: boolean;
  };
}

const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  isDeleting = false,
  deleteButtonText = "Delete",
  itemType = "item",
  customStyling
}) => {
  if (!isOpen) return null;

  const handleConfirm = () => {
    if (!isDeleting) {
      onConfirm();
    }
  };

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md">
        <div className="bg-[#111111] border border-gray-800 rounded-lg p-8 shadow-xl relative">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-white transition"
            aria-label="Close"
            disabled={isDeleting}
          >
            <X className="w-6 h-6" />
          </button>

          {/* Icon and Title */}
          <div className="flex flex-col items-center text-center mb-6">
            <div className={`w-16 h-16 ${customStyling?.iconBg || 'bg-red-500/10'} rounded-full flex items-center justify-center mb-4`}>
              {customStyling?.useSignIcon ? (
                <PenTool className={`w-8 h-8 ${customStyling?.iconColor || 'text-red-500'}`} />
              ) : (
                <AlertTriangle className={`w-8 h-8 ${customStyling?.iconColor || 'text-red-500'}`} />
              )}
            </div>
            
            <h2 className="text-xl font-bold text-white mb-2">
              {title}
            </h2>
            
            <p className="text-gray-400 text-sm leading-relaxed">
              {message}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              disabled={isDeleting}
              className="flex-1 px-4 py-3 rounded-lg border border-gray-600 text-gray-300 hover:text-white hover:border-gray-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            
            <button
              onClick={handleConfirm}
              disabled={isDeleting}
              className={`flex-1 px-4 py-3 rounded-lg ${customStyling?.confirmButtonBg || 'bg-red-600 hover:bg-red-700'} ${customStyling?.confirmButtonText || 'text-white'} transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2`}
            >
              {isDeleting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Deleting...</span>
                </>
              ) : (
                <>
                  {customStyling?.useSignIcon ? (
                    <PenTool className="w-4 h-4" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                  <span>{deleteButtonText}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteConfirmationModal;
